import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import { API } from "@/shared/api";
import type { ThemeConfig, ThemeName } from "../types/themeConfig";

const STYLE_ID = "theme-override";
const CONFIG_PATH = `${import.meta.env.VITE_PUBLIC_PATH ?? "/"}assets/theme/theme-config.json`;

const NAMED_THEMES: ThemeName[] = [
	"default",
	"green",
	"blue",
	"pink",
	"custom",
];

export async function fetchThemeConfig(): Promise<ThemeConfig | null> {
	try {
		const res = await fetch(CONFIG_PATH, { cache: "no-store" });
		if (!res.ok) return null;
		return (await res.json()) as ThemeConfig;
	} catch {
		return null;
	}
}

export async function saveThemeConfig(
	config: ThemeConfig,
): Promise<ThemeConfig> {
	return ItiHttpService.post(API.customizations.themeConfig, config);
}

export async function saveThemeConfigLogos(
	logoLogin: File | null,
	logoMenu: File | null,
): Promise<void> {
	const formData = new FormData();
	if (logoLogin) formData.append("logoLogin", logoLogin);
	if (logoMenu) formData.append("logoMenu", logoMenu);
	return ItiHttpService.postMultiPart(
		API.customizations.themeConfigLogos,
		formData,
	);
}

export async function saveFavicon(favicon: File): Promise<void> {
	const formData = new FormData();
	formData.append("favicon", favicon);
	return ItiHttpService.postMultiPart(API.customizations.favicon, formData);
}

export function applyThemeConfig(config: ThemeConfig): void {
	const theme = NAMED_THEMES.includes(config.theme) ? config.theme : "default";

	if (theme !== "custom") {
		document.documentElement.setAttribute(
			"data-color-scheme",
			theme === "default" ? "" : theme,
		);
		removeThemeOverride();
		return;
	}

	document.documentElement.setAttribute("data-color-scheme", "custom");

	const entries = Object.entries(config.colors)
		.filter(([, value]) => Boolean(value))
		.map(([prop, value]) => `  ${prop}: ${value};`)
		.join("\n");

	if (!entries) return;

	const css = [
		`[data-color-scheme="custom"] {\n${entries}\n}`,
		`[data-color-scheme="custom"][data-theme="dark"] {\n${entries}\n}`,
	].join("\n");

	let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
	if (!styleEl) {
		styleEl = document.createElement("style");
		styleEl.id = STYLE_ID;
		document.head.appendChild(styleEl);
	}
	styleEl.textContent = css;
}

function removeThemeOverride(): void {
	document.getElementById(STYLE_ID)?.remove();
}
